import { useEffect, useState } from 'react';
import { useBusinessFunctionService } from 'domain/shared/businessFunction';
import { useTechnologyService } from 'domain/shared/career';
import {
  OFFICE_NAMES,
  OfficeType,
  type PositionType,
  useOfficeService,
  usePositionService,
} from 'domain/shared/employee';
import { EMPLOYEE_COMPETENCY_LEVEL } from 'domain/shared/performanceMeasurement';
import {
  type FilterBarFilterConfig,
  type FilterBarFilterOption,
  type FilterBarFiltersProps,
  type FilterBarSearchInputProps,
  type FilterState,
} from 'framework/ViewTypes/components';
import { FilterCondition } from 'framework/types';
import { POSITION_SLOT_LIFECYCLE_LABELS, POSITION_SLOT_WORKER_TYPE_LABELS } from '../constants';

interface UsePositionSlotsFilterBarParams {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  resetFilters: () => void;
  excludedFilterIds?: PositionSlotFilterId[];
}

type PositionSlotFilterId =
  | 'specializationId'
  | 'technologyId'
  | 'businessFunctionId'
  | 'competencyLevel'
  | 'lifecycle'
  | 'workerType'
  | 'officeIds';

const usePositionSlotsFilterBar = ({
  filters,
  setFilters,
  searchValue,
  setSearchValue,
  resetFilters,
  excludedFilterIds = [],
}: UsePositionSlotsFilterBarParams): {
  searchProps: FilterBarSearchInputProps;
  filterProps: FilterBarFiltersProps;
} => {
  const { findAll: findAllPositions } = usePositionService();
  const { findAll: findAllTechnologies } = useTechnologyService();
  const { findAll: findAllBusinessFunctions } = useBusinessFunctionService();
  const { findAll: findAllOffices } = useOfficeService();
  const [specializationOptions, setSpecializationOptions] = useState<FilterBarFilterOption[]>([]);
  const [technologyOptions, setTechnologyOptions] = useState<FilterBarFilterOption[]>([]);
  const [businessFunctionOptions, setBusinessFunctionOptions] = useState<FilterBarFilterOption[]>([]);
  const [officeOptions, setOfficeOptions] = useState<FilterBarFilterOption[]>([]);
  const excludedFilterIdsKey = [...excludedFilterIds].sort().join('|');

  useEffect(() => {
    let isActive = true;
    const excludedFilters = new Set(excludedFilterIdsKey.split('|'));

    Promise.all([
      excludedFilters.has('specializationId') ? Promise.resolve([]) : findAllPositions(),
      excludedFilters.has('technologyId') ? Promise.resolve([]) : findAllTechnologies(),
      excludedFilters.has('businessFunctionId') ? Promise.resolve([]) : findAllBusinessFunctions(),
      excludedFilters.has('officeIds') ? Promise.resolve([]) : findAllOffices(),
    ])
      .then(([positions, technologies, businessFunctions, offices]) => {
        if (!isActive) return;

        setSpecializationOptions(
          positions.map((position: PositionType) => ({ value: String(position.id), label: position.name })),
        );
        setTechnologyOptions(technologies.map(technology => ({ value: technology.id, label: technology.name })));
        setBusinessFunctionOptions(
          businessFunctions.map(businessFunction => ({ value: businessFunction.id, label: businessFunction.name })),
        );
        setOfficeOptions(
          offices.map((office: OfficeType) => ({
            value: String(office.id),
            label: OFFICE_NAMES[office.name] ?? office.name,
          })),
        );
      })
      .catch(() => {
        if (!isActive) return;

        setSpecializationOptions([]);
        setTechnologyOptions([]);
        setBusinessFunctionOptions([]);
        setOfficeOptions([]);
      });

    return () => {
      isActive = false;
    };
    // Dictionary services are recreated with their API hooks. The reference change is not a reload signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludedFilterIdsKey]);

  const availableFilters: FilterBarFilterConfig[] = [
    {
      id: 'specializationId',
      source: 'specializationId',
      condition: FilterCondition.IN,
      type: 'dropdown',
      label: 'Specialization',
      multiple: true,
      options: specializationOptions,
    },
    {
      id: 'technologyId',
      source: 'technologyId',
      condition: FilterCondition.IN,
      type: 'dropdown',
      label: 'Technology',
      multiple: true,
      options: technologyOptions,
    },
    {
      id: 'businessFunctionId',
      source: 'businessFunctionId',
      condition: FilterCondition.IN,
      type: 'dropdown',
      label: 'Business function',
      multiple: true,
      options: businessFunctionOptions,
    },
    {
      id: 'competencyLevel',
      source: 'competencyLevel',
      condition: FilterCondition.IN,
      type: 'dropdown',
      label: 'Level',
      multiple: true,
      options: Object.entries(EMPLOYEE_COMPETENCY_LEVEL).map(([value, label]) => ({ value, label })),
    },
    {
      id: 'lifecycle',
      source: 'lifecycle',
      condition: FilterCondition.IN,
      type: 'dropdown',
      label: 'Status',
      multiple: true,
      options: Object.entries(POSITION_SLOT_LIFECYCLE_LABELS).map(([optionValue, label]) => ({
        value: optionValue,
        label,
      })),
    },
    {
      id: 'workerType',
      source: 'workerType',
      condition: FilterCondition.IN,
      type: 'dropdown',
      label: 'Category',
      multiple: true,
      options: Object.entries(POSITION_SLOT_WORKER_TYPE_LABELS).map(([optionValue, label]) => ({
        value: optionValue,
        label,
      })),
    },
    {
      id: 'officeIds',
      source: 'officeIds',
      condition: FilterCondition.IN,
      type: 'dropdown',
      label: 'Location',
      multiple: true,
      options: officeOptions,
    },
  ];

  return {
    searchProps: {
      label: 'Search',
      placeholder: 'Search',
      value: searchValue,
      onChange: setSearchValue,
    },
    filterProps: {
      value: filters,
      onChange: setFilters,
      onReset: resetFilters,
      filters: availableFilters.filter(filter => !excludedFilterIds.includes(filter.id as PositionSlotFilterId)),
    },
  };
};

export default usePositionSlotsFilterBar;
